import {
  Box,
  Flex,
  HStack,
  Link,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DataHandler } from "../DataHandler";
import { Splash } from "../Splash";
import RatingStars from "../StarRate";
import { Review } from "./Review";
import { ReviewModal } from "./ReviewModal";

/**
 * @typedef {object} Props
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/Post").Post[]>>} swrResponsePostArticleReviews
 */

/**
 * @param {Props} props
 */
export function OurClientsReview({ swrResponsePostArticleReviews }) {
  const intl = useIntl();

  const [selectedReview, setSelectedReview] = useState(
    /** @type {import("@raiden/library/types/Post").Post | null} */
    (null),
  );

  const linkToGoogleReviews =
    "https://www.google.com/search?sca_esv=552fb68e3d08662a&sxsrf=AE3TifNJ-aqgoqhuzexRwvIwzhSm_sug6g:1760514991624&q=valentin+expertises&si=AMgyJEuzsz2NflaaWzrzdpjxXXRaJ2hfdMsbe_mSWso6src8s4BVZA1h8sTDwBZWMN1OA1Y586n0K3YWNa9MtvGDR9J08hisFZMAjXon-wYVgWuJJQvNz4I%3D&uds=AOm0WdHmOFDPI7FIyNbJw6Zwd9kSBy6Dse2CEHV7SxVzTj3FZ4Tq7OagUIWoT75HfwMkTD__Tj-9UJMinQkTF_MMeneWsSWUaNrZjIBDRAhkRFqvYRLJtjg&sa=X&sqi=2&ved=2ahUKEwjjgcq13aWQAxWgLPsDHeRWDx8Q3PALegQIHhAE&biw=1920&bih=993&dpr=1";

  const reviewAverageNote =
    process.env.NEXT_PUBLIC_GOOGLE_REVIEW_AVERAGE_NOTE ?? "4.7";

  const countReviews =
    process.env.NEXT_PUBLIC_GOOGLE_REVIEW_COUNT_REVIEWS ?? 60;

  return (
    <DataHandler
      swrResponse={swrResponsePostArticleReviews}
      keepDataOnRevalidation={true}
      noResult={
        <Flex alignItems="center" flexDir="column" gap="1rem" mx="auto">
          <Box width={{ base: "200px", lg: "700px" }}>
            <Splash
              src={generateFrontPath({
                id: "@front.internal-assets",
                parameters: { filePath: "illustrations/error.svg" },
                includeBasePath: true,
              })}
              title={intl.formatMessage({
                defaultMessage: "Erreur lors du chargement de la page",
              })}
            />
          </Box>
        </Flex>
      }>
      {({ data: posts }) => {
        const sortedByPositionPosts = posts.sort((a, b) => {
          if (a.position && b.position) {
            return a.position - b.position;
          }

          return 0;
        });

        return (
          <>
            <Stack spacing="1.5rem">
              <Text variant="h3">
                <FormattedMessage defaultMessage="Les avis de nos clients" />
              </Text>

              <HStack spacing="0.5rem">
                <Text color="brandSecondary.600">
                  <FormattedMessage
                    defaultMessage="Moyenne {average}"
                    values={{
                      average:
                        Number(reviewAverageNote) > 5 ? "5" : reviewAverageNote,
                    }}
                  />
                </Text>

                <RatingStars rate={parseFloat(reviewAverageNote)} />

                <Link
                  href={linkToGoogleReviews}
                  isExternal={true}
                  textDecoration="underline"
                  variant="text-button"
                  color="brandPrimary.500"
                  rel="noopener noreferrer">
                  <FormattedMessage
                    defaultMessage="Voir les {count} avis"
                    values={{ count: countReviews }}
                  />
                </Link>
              </HStack>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="1.5rem">
                {sortedByPositionPosts.map((post) => (
                  <Review
                    review={post}
                    key={post.id}
                    setSelectedReview={setSelectedReview}
                  />
                ))}
              </SimpleGrid>

              <Box p="1rem" border="1px solid" borderColor="brandPrimary.400">
                <Text color="brandSecondary.600">
                  <FormattedMessage defaultMessage="Les témoignages spontanés sur Google ou Pages jaunes avec une moyenne de 4,7/5 obtenue grâce aux avis authentiques de nos clients (et non une notation flatteuse de 5/5 parfois autoproclamée que s'attribuent certaines sociétés), témoignent de manière transparente de la qualité et du professionnalisme de notre cabinet d'expertise." />
                </Text>
              </Box>
            </Stack>

            <ReviewModal
              review={selectedReview}
              isOpen={selectedReview !== null}
              onClose={() => setSelectedReview(null)}
            />
          </>
        );
      }}
    </DataHandler>
  );
}
